/**
 * Client-side export helpers — no server round-trip.
 * - toCsv(): turn an array of objects into a CSV string (Excel-friendly, BOM + CRLF).
 * - downloadCsv(): trigger a browser download of a CSV file.
 * - copyText(): clipboard copy with a prompt() fallback for non-secure contexts.
 */

/** Escape one CSV field per RFC 4180 (quote if it contains comma/quote/newline). */
function csvField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

/**
 * Build a CSV string from rows. `columns` maps header label -> accessor.
 * Example: toCsv(tasks, { Project: t => t.code, Task: t => t.name })
 */
export function toCsv<T>(rows: T[], columns: Record<string, (row: T) => unknown>): string {
  const headers = Object.keys(columns);
  const lines = [headers.map(csvField).join(',')];
  for (const row of rows) {
    lines.push(headers.map((h) => csvField(columns[h](row))).join(','));
  }
  // BOM so Excel reads UTF-8; CRLF line endings for Excel compatibility.
  return '﻿' + lines.join('\r\n');
}

/** Trigger a download of `content` as `filename`. */
export function downloadFile(content: string, filename: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Convenience: build CSV from rows+columns and download it. */
export function downloadCsv<T>(rows: T[], columns: Record<string, (row: T) => unknown>, filename: string): void {
  downloadFile(toCsv(rows, columns), filename);
}

/** A date stamp like 2026-05-31 for filenames (no Date import elsewhere needed). */
export function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Copy text to clipboard; resolves true on success. Falls back to prompt(). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try { window.prompt('Copy:', text); } catch { /* noop */ }
  return false;
}
